import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { Asterisk } from '@/components/generals/asterisk'
import { ButtonLink } from '@/components/generals/button-link'
import { LocalTime } from '@/components/generals/local-time'
import { SectionLabel } from '@/components/generals/section-label'
import { navLinks, siteConfig } from '@/constants/site'

const footerLinks = [...navLinks, { label: "Contact", href: '/#contact' }]

export const Footer: FC = (): ReactNode => {
    const backToTop = (): void => {
        window.scrollTo({ top: 0 })
    }

    return (
        <footer data-surface='ink' className='relative overflow-hidden bg-foreground text-background'>
            <div className='page-container pt-20 md:pt-28'>
                <div className='grid gap-12 md:grid-cols-12'>
                    <div className='md:col-span-6'>
                        <SectionLabel className='text-background/60' markClassName='text-brand-light'>
                            Next step
                        </SectionLabel>

                        <p className='mt-6 max-w-xl text-balance font-heading text-[clamp(2.5rem,5.5vw,5rem)] font-semibold leading-[0.95] tracking-[-0.04em]'>
                            Have an idea worth <em className='font-serif font-normal italic tracking-[-0.02em] text-brand-light'>shouting</em> about?
                        </p>

                        <ButtonLink href='/#contact' variant='paper' size='lg' className='mt-8'>
                            Start a project
                        </ButtonLink>
                    </div>

                    <nav aria-label="Footer" className='md:col-span-2 md:col-start-8'>
                        <p className='font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-background/50'>
                            Menu
                        </p>

                        <ul className='mt-5 space-y-2.5'>
                            {footerLinks.map(link => (
                                <li key={link.href}>
                                    <Link href={link.href} className='text-background/80 transition-colors hover:text-background'>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='md:col-span-2'>
                        <p className='font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-background/50'>
                            Elsewhere
                        </p>

                        <ul className='mt-5 space-y-2.5'>
                            {siteConfig.socials.map(social => (
                                <li key={social.label}>
                                    <a
                                        href={social.href}
                                        target='_blank'
                                        rel='noreferrer'
                                        className='group inline-flex items-center gap-1 text-background/80 transition-colors hover:text-background'
                                    >
                                        {social.label}
                                        <ArrowUpRight className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='md:col-span-2'>
                        <p className='font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-background/50'>
                            Studio
                        </p>

                        <p className='mt-5 text-background/80'>
                            {siteConfig.location}
                        </p>

                        <p className='mt-1.5 text-sm text-background/60'>
                            Local time <LocalTime className='font-mono text-background/80' />
                        </p>
                    </div>
                </div>

                <p
                    aria-hidden='true'
                    className='mt-20 flex select-none items-start whitespace-nowrap font-heading text-[16vw] font-semibold leading-[0.8] tracking-[-0.06em] md:mt-28'
                >
                    {siteConfig.name}
                    <Asterisk className='ml-[0.04em] mt-[0.06em] size-[0.32em] shrink-0 text-brand-light' />
                </p>

                <div className='mt-8 flex flex-col gap-4 border-t border-background/10 py-6 text-sm text-background/55 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. Designed and built in Lisbon.
                    </p>

                    <button
                        type='button'
                        onClick={backToTop}
                        className='inline-flex items-center gap-2 self-start transition-colors hover:text-background sm:self-auto'
                    >
                        Back to top
                        <ArrowUp className='size-4' />
                    </button>
                </div>
            </div>
        </footer>
    )
}
